// LifecycleStrip: five key moments of a product's life, unboxing to recycling (of the seven in
// content.lifecycle; fewer to choose between). One stage is active and advances slowly (progress bar on the track); each stage shows the owner's moment, where AI helps,
// an honest status, and a small phone vignette of that moment for the unit. Pure React/CSS.
import { useEffect, useRef, useState } from 'react';
import { lifecycle } from '../content';
import { cx } from '../ui';
import type { HeroUnit } from '../heroUnits';
import { defaultUnit, unitLife, type Tone } from './data';
import { Photo, useAiStyles, useOnScreen } from './shared';
import { prefersReducedMotion } from '../ui';
import { Device, PassportScreen, RegisterScreen, ServiceScreen, SetupScreen, UseScreen } from '../phone';
import { sectorByKey, type Sector } from '../variants/c/sectors';

function StageScreen({ k, unit, sector }: { k: StageKey; unit: HeroUnit; sector: Sector }) {
  const life = unitLife[unit.key];
  if (k === 'Register') return <RegisterScreen s={sector} />;
  if (k === 'Unbox') return <SetupScreen s={sector} steps={life.setup} />;
  if (k === 'Use') return <UseScreen s={sector} tip={life.tip} q="What setting should I use?" a={unit.answer} src={unit.source} />;
  if (k === 'Service') return <ServiceScreen s={sector} />;
  if (k === 'Recycle') return <PassportScreen s={sector} />;
  return null;
}

export type LifecycleStripProps = {
  tone?: Tone;
  unit?: HeroUnit;
  className?: string;
  interval?: number;
  /** Optional editorial photo behind the phone (desktop). */
  photo?: { src: string; alt: string };
  /** Which stages to show, by key. Defaults to the five that carry the story. */
  only?: StageKey[];
};

type StageKey = (typeof lifecycle.stages)[number]['k'];

// Unbox (register in two taps), set up, use, keep it working, recycle: the moments every owner has (user, 20 Oct).
const KEY_STAGES: StageKey[] = ['Register', 'Unbox', 'Use', 'Service', 'Recycle'];
const LABEL: Partial<Record<StageKey, string>> = { Register: 'Unbox', Unbox: 'Set up', Service: 'Maintain' };
const label = (k: StageKey) => LABEL[k] ?? k;

export function LifecycleStrip({ tone = 'light', unit = defaultUnit, className, interval = 5600, photo, only = KEY_STAGES }: LifecycleStripProps) {
  useAiStyles();
  const stages = only.map((k) => lifecycle.stages.find((st) => st.k === k)!).filter(Boolean);
  const [active, setActive] = useState(0);
  const [prog, setProg] = useState(0);
  const outerRef = useRef<HTMLDivElement>(null);
  // Desktop: the section holds still and scrolling moves through the stages.
  const [pin, setPin] = useState(false);
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const mq = window.matchMedia('(min-width: 1024px) and (min-height: 720px)');
    const on = () => setPin(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  useEffect(() => {
    if (!pin) return;
    const n = stages.length;
    const tick = () => {
      const el = outerRef.current;
      if (!el) return;
      const total = el.offsetHeight - window.innerHeight;
      const p = Math.min(1, Math.max(0, -el.getBoundingClientRect().top / Math.max(1, total)));
      const i = Math.min(n - 1, Math.floor(p * n));
      setActive(i);
      setProg(Math.min(1, p * n - i));
    };
    tick();
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick);
    return () => {
      window.removeEventListener('scroll', tick);
      window.removeEventListener('resize', tick);
    };
  }, [pin, stages.length]);
  const goTo = (i: number) => {
    const el = outerRef.current;
    if (!pin || !el) return false;
    const total = el.offsetHeight - window.innerHeight;
    const top = el.getBoundingClientRect().top + window.scrollY + (total * (i + 0.15)) / stages.length;
    window.scrollTo({ top, behavior: 'smooth' });
    return true;
  };
  const [paused, setPaused] = useState(false);
  const [reduced] = useState(prefersReducedMotion);
  const [ref, onScreen] = useOnScreen<HTMLDivElement>(0.3);
  const trackRef = useRef<HTMLOListElement>(null);
  const running = onScreen && !paused && !reduced && !pin;

  useEffect(() => {
    if (!running) return;
    const t = window.setTimeout(() => setActive((a) => (a + 1) % stages.length), interval);
    return () => window.clearTimeout(t);
  }, [running, active, interval, stages.length]);

  // keep the active stage visible on the mobile scroll track, without moving the page
  useEffect(() => {
    const track = trackRef.current;
    const el = track?.children[active] as HTMLElement | undefined;
    if (!track || !el || track.scrollWidth <= track.clientWidth) return;
    const left = track.scrollLeft + el.getBoundingClientRect().left - track.getBoundingClientRect().left - 24;
    track.scrollTo({ left, behavior: reduced ? 'auto' : 'smooth' });
  }, [active, reduced]);

  const s = stages[active];
  const sector = sectorByKey(unit.key);
  const body = (
    <div ref={ref} className={cx('ai-life', pin && 'is-pinned', className)} data-tone={tone} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <ol ref={trackRef} className="ai-track" style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(0,1fr))` }} role="tablist" aria-label="Stages in the life of a product">
        {stages.map((st, i) => (
          <li key={st.k} className="ai-track-li">
            <button
              type="button"
              role="tab"
              id={`ai-stage-${i}`}
              aria-selected={i === active}
              aria-controls="ai-stage-panel"
              className={cx('ai-stage', i === active && 'is-on', i < active && 'is-past')}
              onClick={() => {
                if (goTo(i)) return;
                setActive(i);
                setPaused(true);
              }}
            >
              <span className="ai-stage-n">{String(i + 1).padStart(2, '0')}</span>
              <span className="ai-stage-k">{label(st.k)}</span>
              <span className="ai-stage-bar">
                <i
                  key={`${active}-${i}-${pin}`}
                  style={i !== active ? undefined : pin ? { animation: 'none', transform: `scaleX(${prog})` } : { animationDuration: `${interval}ms`, animationPlayState: running ? 'running' : 'paused' }}
                />
              </span>
            </button>
          </li>
        ))}
      </ol>

      <div id="ai-stage-panel" role="tabpanel" aria-labelledby={`ai-stage-${active}`} className="ai-life-panel">
        <div className="ai-life-copy" key={s.k}>
          <div className="ai-life-k">
            {String(active + 1).padStart(2, '0')} · {label(s.k)}
          </div>
          <h3 className="ai-life-t">{s.t}</h3>
          <p className="ai-life-d">{s.d}</p>
          <div className="ai-life-ai">
            <span className="ai-life-ai-l">Where AI helps</span>
            <span className="ai-life-ai-t">{s.ai}</span>
          </div>
        </div>
        <div className={cx('ai-life-phone', photo && 'has-photo')}>
          {photo && <Photo src={photo.src} alt={photo.alt} className="ai-life-photo" width={1600} height={1067} />}
          <Device s={sector} className="ai-life-dev" label={`${label(s.k)}: ${s.t}, on the owner's phone for the ${unit.model}`}>
            <div className="ai-life-screen" key={`${unit.key}-${s.k}`}>
              <StageScreen k={s.k} unit={unit} sector={sector} />
            </div>
          </Device>
        </div>
      </div>
    </div>
  );
  if (!pin) return body;
  return (
    <div ref={outerRef} className="ai-life-pin" style={{ height: `${stages.length * 85 + 15}vh` }}>
      <div className="ai-life-stick">{body}</div>
    </div>
  );
}

export default LifecycleStrip;
